import { UHttp } from "../../utils/UHttp";

export type BookingStatus = 'confirmed' | 'cancelled' | 'completed';

export interface BookingOption {
  id: string;
  name: string;
  price: number;
  categoryId: string;
}

export interface BookingResponse {
  success: boolean;
  message: string;
  status:number;
  data?: BookingListItem[] |BookingListItem | null;
  requiresPayment?: boolean;
  checkoutUrl?: string;
}


export interface BookingService {
  id?: string;
  serviceId: string;
  serviceName: string;
  orderIndex: number;
  optionIds?: string[];
  price: number;
  duration: number;
  status?: 'active' | 'cancelled';
  cancelledAt?: string | null;
  refundAmount?: number | null;
  options?: BookingOption[];
}

export interface BookingListItem {
  id: string;
  status: BookingStatus;
  startTime: string;
  endTime: string;
  timeSlotId: string;
  // Legacy single service fields (for backward compatibility)
  serviceName?: string;
  serviceId?: string;
  serviceDuration?: number;
  servicePrice?: number;
  // Multi-services fields
  services?: BookingService[];
  totalDuration?: number;
  businessName: string;
  collaboratorCompagnieId: string;
  collaboratorName?: string;
  clientName: string;
  clientFirstName: string;
  clientLastName: string;
  clientEmail: string;
  clientPhone?: string;
  totalPrice: number;
  paidAmount?: number;
  paymentType?: 'full' | 'deposit';
  options?: BookingOption[];
  createdAt?: string;
}

export interface BookingCreateParams {
  // Legacy single service format (deprecated)
  serviceId?: string;
  optionIds?: string[];
  // Multi-services format
  services?: Array<{
    serviceId: string;
    optionIds?: string[];
    price: number;
    duration: number;
  }>;
  collaboratorCompagnieId: string;
  timeSlotId?: string;
  startTime?: string;
  endTime?: string;
  clientId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  notes?: string;
}

const apiUrlServer = process.env.REACT_APP_URL_SERVER;

export class ClientBookingModel {
  private static instance: ClientBookingModel;

  private constructor() {}

  public static getInstance(): ClientBookingModel {
    if (!ClientBookingModel.instance) {
      ClientBookingModel.instance = new ClientBookingModel();
    }
    return ClientBookingModel.instance;
  }

  /**
   * Récupère une réservation par son ID
   */
  public async getBookingById(id: string): Promise<BookingResponse> {
    try {
      const response = await UHttp.get(
        `${apiUrlServer}/public-api/booking/client/${id}`,
        UHttp.publicApiHeaders()
      );
      const responseData = await response.json();
      return {
        ...responseData,
        status: response.status
      } as BookingResponse

    } catch (error) {
      throw error;
    }
  }

  /**
   * Crée une nouvelle réservation
   */
  public async createBooking(params: BookingCreateParams): Promise<BookingResponse> {
    try {
      const response = await UHttp.post(
        `${apiUrlServer}/public-api/booking/client/`,
        params,
        UHttp.publicApiHeaders()
      );
      const responseData = await response.json();
      return {
        ...responseData,
        status: response.status
      } as BookingResponse
    } catch (error) {
      throw error;
    }
  }


  /**
   * Annule une réservation par son id
   */
  public async cancelBooking(id: string): Promise<BookingResponse> {
    try {
      const response = await UHttp.post(
        `${apiUrlServer}/public-api/booking/client/cancel/${id}`,
          {},
          UHttp.publicApiHeaders()
      );
      const responseData = await response.json();
      return {
        ...responseData,
        status: response.status
      } as BookingResponse
    } catch (error) {
      throw error;
    }
  }

  /**
   * Met à jour une réservation existante
   */
  public async updateBooking(
    id: string,
    updateData: {
      services?: Array<{
        serviceId: string;
        optionIds?: string[];
        price?: number;
        duration?: number;
      }>;
      startTime?: string;
      endTime?: string;
      timeSlotId?: string;
    }
  ): Promise<BookingResponse> {
    try {
      const response = await UHttp.put(
        `${apiUrlServer}/public-api/booking/client/update/${id}`,
        updateData,
        UHttp.publicApiHeaders()
      );
      const responseData = await response.json();
      return {
        ...responseData,
        status: response.status
      } as BookingResponse;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Vérifie si le paiement en ligne est requis pour une entreprise
   */
  public async checkOnlinePaymentRequired(businessName: string): Promise<{ success: boolean; message: string; data?: { onlinePaymentRequired: boolean; stripeConnected: boolean } }> {
    try {
      const response = await UHttp.get(
        `${apiUrlServer}/public-api/service/payment-required/${businessName}`,
        UHttp.publicApiHeaders()
      );
      const responseData = await response.json();
      return responseData;
    } catch (error) {
      console.error('Error checking payment requirement:', error);
      return {
        success: false,
        message: 'Error checking payment requirement'
      };
    }
  }
}
